import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Unstable_Grid2";
import { Paper, Typography, Button, Box } from "@mui/material";
import { Search, Refresh, Add as AddIcon } from "@mui/icons-material";
import { DataTable } from "../../common/dataTable/DataTable";
import { initLocalization } from "../../../utils/helpers";
import { BisListingNotMappedStrings } from "./locale";
import { BisListingNotMappedFilters } from "../bisListingNotMappedFilters/BisListingNotMappedFilters";
import useGetNotMappedBisList from "../../../hooks/media/useGetNotMappedBisList";
import useAssociateMapping from "../../../hooks/media/useAssociateMapping";
import { DEFAULT_BIS_FILTERS } from "../../../constants";

export const BisListingNotMapped = ({
  selectedMedia,
  onSelectedBISNotMapped,
  onRefreshMapped,
  refreshKey,
  isLoading = false,
}) => {
  initLocalization(BisListingNotMappedStrings);

  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const {
    isLoading: isAssociating,
    error,
    associateMapping,
  } = useAssociateMapping();

  const [filters, setFilters] = useState(DEFAULT_BIS_FILTERS);

  const {
    mediaMappingList: bisNotMapped,
    getNotMappedBisList: getBisNotMapped,
    isLoading: isBisNotMappedLoading,
  } = useGetNotMappedBisList(false, false);
  useEffect(() => {
    if (
      !selectedMedia?.mediaId ||
      !selectedMedia?.displayTypeId ||
      !selectedMedia?.dataProviderId
    )
      return;
    handleSearch();
  }, [
    selectedMedia?.mediaId,
    selectedMedia?.displayTypeId,
    selectedMedia?.dataProviderId,
    refreshKey,
  ]);

  const handleSearch = () => {
    if (!selectedMedia?.dataProviderId || !selectedMedia?.displayTypeId) return;

    const payload = {
      includeMapped: false,
      displayTypeId: selectedMedia.displayTypeId,
      dataProviderId: selectedMedia.dataProviderId,
      bisPublisherId: filters.idbis ? Number(filters.idbis) : undefined,
      bisPublisherLabel: filters.libellebis || undefined,
    };

    getBisNotMapped(payload);
  };

  const handleReset = () => {
    setFilters(DEFAULT_BIS_FILTERS);

    if (selectedMedia?.displayTypeId && selectedMedia?.dataProviderId) {
      getBisNotMapped({
        includeMapped: false,
        displayTypeId: selectedMedia.displayTypeId,
        dataProviderId: selectedMedia.dataProviderId,
      });
    }
  };

  const rows = useMemo(() => {
    return (bisNotMapped || []).map((item) => ({
      id: item.id,
      IdBIS: item.publisherId || "-",
      LabelBIS: item.ytPublisherChannelName || item.publisherDomain || "-",
    }));
  }, [bisNotMapped]);

  const handleSelectRow = (event, index) => {
    const row = rows[index];
    if (!row) return;

    const updatedSelectedRows = new Set(selectedRows);
    if (updatedSelectedRows.has(row.id)) {
      updatedSelectedRows.delete(row.id);
    } else {
      updatedSelectedRows.add(row.id);
    }

    setSelectedRows(updatedSelectedRows);
  };
  const handleAssociate = () => {
    if (onSelectedBISNotMapped) {
      onSelectedBISNotMapped([...selectedRows]);
    }

    if (selectedMedia?.mediaId) {
      associateMapping(selectedMedia.mediaId, [...selectedRows], () => {
        getBisNotMapped({
          includeMapped: false,
          mediaId: selectedMedia.mediaId,
          displayTypeId: selectedMedia.displayTypeId,
          dataProviderId: selectedMedia.dataProviderId,
        });

        setSelectedRows(new Set());

        if (onRefreshMapped) {
          onRefreshMapped();
        }
      });
    }
  };

  const columns = [
    {
      field: "IdBIS",
      headerName: BisListingNotMappedStrings.IdBIS,
      minWidth: 30,
    },
    {
      field: "LabelBIS",
      headerName: BisListingNotMappedStrings.LabelBIS,
      minWidth: 50,
    },
  ];

  return (
    <Paper sx={{ padding: "0.5rem", height: "100%", position: "relative" }}>
      <Grid container direction="column" height="100%">
        <Grid
          xs={12}
          paddingBottom={2}
          paddingTop={1}
          paddingLeft={1}
          display="flex"
          justifyContent="space-between"
        >
          <Typography data-testid="advertiser-listing-title">
            {BisListingNotMappedStrings.title}
          </Typography>
          <Button
            startIcon={<Refresh sx={{ transform: "scaleX(-1)" }} />}
            variant="text"
            onClick={handleReset}
          >
            {BisListingNotMappedStrings.resetFiltersButton}
          </Button>
        </Grid>

        <Grid
          container
          spacing={2}
          alignItems="center"
          paddingX={1}
          paddingBottom={2}
        >
          <Grid xs={12} sm={10}>
            <BisListingNotMappedFilters
              filters={filters}
              setFilters={setFilters}
            />
          </Grid>
          <Grid xs={12} sm={2} textAlign={{ xs: "left", sm: "right" }}>
            <Button
              startIcon={<Search />}
              variant="contained"
              color="secondary"
              fullWidth
              sx={{ mt: { xs: 1, sm: 0 } }}
              onClick={handleSearch}
            >
              {BisListingNotMappedStrings.search}
            </Button>
          </Grid>
        </Grid>

        <Grid
          xs={12}
          display="flex"
          flexDirection="column"
          flexGrow={1}
          sx={{ overflow: "auto" }}
        >
          <DataTable
            selectedRows={[...selectedRows]}
            columns={columns}
            rows={rows}
            showPagination={false}
            allowSelection={true}
            showNoDataMessage={!isBisNotMappedLoading}
            tableMinWidth={750}
            onSelectRow={handleSelectRow}
          />
        </Grid>

        <Grid container justifyContent="flex-end" mt={2}>
          <Grid>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              size="large"
              color="primary"
              disabled={selectedRows.size === 0 || isAssociating}
              onClick={handleAssociate}
            >
              {isAssociating
                ? BisListingNotMappedStrings.associationInProgress
                : BisListingNotMappedStrings.associer}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

BisListingNotMapped.propTypes = {
  selectedMedia: PropTypes.object.isRequired,
  onSelectedBISNotMapped: PropTypes.func,
  onRefreshMapped: PropTypes.func,
  refreshKey: PropTypes.number,
  isLoading: PropTypes.bool,
};

export default BisListingNotMapped;
